import { useState } from "react";
import { anonymousThoughts } from "@/mocks/anonymousThoughts";

export default function AnonymousThoughts() {
  const [thought, setThought] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [displayedThoughts, setDisplayedThoughts] = useState(anonymousThoughts);
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const trimmed = thought.trim();
    if (trimmed && trimmed.length <= 500) {
      const newThought = {
        id: `new-${Date.now()}`,
        content: trimmed,
        date: new Date().toLocaleDateString("en-US", {
          month: "long",
          day: "numeric",
          year: "numeric",
        }),
      };
      // Optimistically show the thought immediately.
      setDisplayedThoughts((prev) => [newThought, ...prev]);
      setThought("");
      setCharCount(0);
      setSubmitted(true);
      setTimeout(() => setSubmitted(false), 4000);

      // Submit to Netlify Forms so the submission is actually recorded.
      const params = new URLSearchParams();
      params.append("form-name", "anonymous-thoughts");
      params.append("thought", trimmed);
      try {
        await fetch("/", {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: params.toString(),
        });
      } catch {
        // Optimistic UI already updated; ignore network errors.
      }
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    if (value.length <= 500) {
      setThought(value);
      setCharCount(value.length);
    }
  };

  return (
    <section className="bg-[#f8fcfc] py-16 md:py-24">
      <div className="max-w-4xl mx-auto px-6 md:px-10">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 text-[#418b86] text-xs font-medium tracking-widest uppercase mb-4">
            <i className="ri-heart-3-line text-sm"></i>
            Community Reflections
          </div>
          <h2
            className="text-3xl md:text-4xl font-light text-[#2d2d2d] leading-snug mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Share Your <span className="italic font-semibold">Thoughts.</span>
          </h2>
          <p className="text-[#6b6b6b] text-sm leading-relaxed max-w-lg mx-auto">
            This is a safe space. No names, no emails — just honest reflections,
            gratitude, and the courage to share. Your words may be the comfort
            someone else needs today.
          </p>
        </div>

        {/* Submission Form */}
        <div className="bg-white rounded-2xl border border-[#e8f4f3] p-6 md:p-8 mb-14">
          <div className="flex items-center gap-2 mb-5">
            <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#e6f6f5]">
              <i className="ri-message-3-line text-[#4db8b1] text-sm"></i>
            </div>
            <h3
              className="text-lg font-medium text-[#2d2d2d]"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Write Anonymously
            </h3>
          </div>

          <form
            id="anonymous-thoughts-form"
            name="anonymous-thoughts"
            method="POST"
            data-netlify="true"
            netlify-honeypot="bot-field"
            onSubmit={handleSubmit}
          >
            <input type="hidden" name="form-name" value="anonymous-thoughts" />
            <p className="hidden">
              <label>
                Don't fill this out if you're human: <input name="bot-field" />
              </label>
            </p>
            <textarea
              name="thought"
              value={thought}
              onChange={handleChange}
              placeholder="What resonated with you? What are you working through? Share anything that feels right..."
              className="w-full h-36 bg-[#f8fcfc] border border-[#e8f4f3] rounded-xl p-4 text-sm text-[#2d2d2d] placeholder-[#a0a0a0] resize-none focus:outline-none focus:border-[#418b86] focus:ring-1 focus:ring-[#418b86] transition-all"
              required
            />
            <div className="flex items-center justify-between mt-3">
              <span className="text-[#9a9a9a] text-xs">
                {charCount}/500 characters
              </span>
              <button
                type="submit"
                className="inline-flex items-center gap-2 bg-[#418b86] hover:bg-[#4db8b1] text-white text-sm font-medium px-6 py-3 rounded-full transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-send-plane-line text-sm"></i>
                Share Thought
              </button>
            </div>
          </form>

          {/* Success Message */}
          {submitted && (
            <div className="mt-4 flex items-center gap-2 bg-[#e6f6f5] text-[#418b86] text-sm px-4 py-3 rounded-xl">
              <i className="ri-check-line text-sm"></i>
              Your thought has been shared. Thank you for your courage.
            </div>
          )}
        </div>

        {/* Anonymous Thoughts List */}
        <div className="space-y-6">
          <div className="flex items-center gap-3 mb-2">
            <span className="text-[#418b86] text-xs font-medium tracking-widest uppercase">
              Shared Reflections
            </span>
            <span className="w-1 h-1 rounded-full bg-[#c8e8e6]"></span>
            <span className="text-[#9a9a9a] text-xs">
              {displayedThoughts.length} thoughts
            </span>
          </div>

          {displayedThoughts.map((t) => (
            <div
              key={t.id}
              className="bg-white rounded-2xl border border-[#e8f4f3] p-6 md:p-8 hover:border-[#c8e8e6] transition-all duration-300"
            >
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#e6f6f5] shrink-0">
                  <i className="ri-user-heart-line text-[#4db8b1] text-sm"></i>
                </div>
                <div className="flex-1">
                  <p className="text-[#4a4a4a] text-sm leading-relaxed mb-4">
                    {t.content}
                  </p>
                  <div className="flex items-center gap-2 text-[#9a9a9a] text-xs">
                    <i className="ri-calendar-line text-sm"></i>
                    <span>{t.date}</span>
                    <span className="w-1 h-1 rounded-full bg-[#c8e8e6]"></span>
                    <span className="text-[#418b86] font-medium">
                      Anonymous
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Privacy Note */}
        <div className="mt-10 flex items-start gap-3 bg-[#e6f6f5] rounded-xl p-4">
          <i className="ri-shield-check-line text-[#418b86] text-sm mt-0.5"></i>
          <p className="text-[#5a5a5a] text-xs leading-relaxed">
            <strong className="text-[#418b86]">Your privacy is protected.</strong>{" "}
            No name, email, IP address, or any identifying information is collected
            or displayed with your thought. What you share here stays completely
            anonymous.
          </p>
        </div>
      </div>
    </section>
  );
}